import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Polygon, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import { COLORS, DARK_THEME } from '../../constants/colors';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import 'leaflet/dist/leaflet.css';
import './Map.css';
import mapHeroImage from '../../assets/pages-hero/map.png';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Harita verileri site içinden sunulur (eski ArcGIS servisi kimlik doğrulamaya kapatıldığı için)
const LAYER_DATA_FILES = {
  0: 'turkic-states',
  1: 'tourism-sites',
  2: 'silk-road',
  3: 'islamic-heritage',
};

// Layer configurations (name: English, nameTr: Turkish — resolved at render time via language)
const LAYERS = {
  turkicStates: { id: 0, name: 'Turkic States', nameTr: 'Türk Devletleri', icon: '🏛️', color: '#E30A17' },
  tourism: { id: 1, name: 'Tourism Sites', nameTr: 'Turizm Noktaları', icon: '📸', color: '#00A9E0' },
  silkRoad: { id: 2, name: 'Silk Road Routes', nameTr: 'İpek Yolu Rotaları', icon: '🛤️', color: '#FFD700' },
  islamicHeritage: { id: 3, name: 'Islamic Heritage', nameTr: 'İslami Miras', icon: '🕌', color: '#2E8B57' },
};

// Helper to determine icon based on type/name
const getTourismIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('mosque') || t.includes('cami') || t.includes('masjid')) return '🕌';
  if (t.includes('museum') || t.includes('müze')) return '🏛️';
  if (t.includes('nature') || t.includes('park') || t.includes('lake') || t.includes('mountain') || t.includes('doğa') || t.includes('göl') || t.includes('dağ') || t.includes('canyon') || t.includes('kanyon')) return '🌲';
  if (t.includes('castle') || t.includes('kale') || t.includes('fortress') || t.includes('saray') || t.includes('palace')) return '🏰';
  if (t.includes('bridge') || t.includes('köprü')) return '🌉';
  if (t.includes('monument') || t.includes('anıt') || t.includes('statue')) return '🗿';
  if (t.includes('ruin') || t.includes('antik') || t.includes('ancient')) return '🏺';
  if (t.includes('bazaar') || t.includes('çarşı') || t.includes('market')) return '🛍️';
  return '📸';
};

// Custom marker creators
const createTourismIcon = (type) => {
  const icon = getTourismIcon(type);
  return L.divIcon({
    className: 'custom-tourism-marker',
    html: `
      <div class="tourism-marker-container">
        <div class="tourism-marker-pulse"></div>
        <div class="tourism-marker-inner">${icon}</div>
      </div>
    `,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
    popupAnchor: [0, -22],
  });
};

const createIslamicIcon = () => {
  return L.divIcon({
    className: 'custom-islamic-marker',
    html: `
      <div class="islamic-marker-container">
        <div class="islamic-marker-glow"></div>
        <div class="islamic-marker-inner">🕌</div>
      </div>
    `,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
    popupAnchor: [0, -24],
  });
};

// Custom cluster icon creators
const createTourismClusterIcon = (cluster) => {
  return L.divIcon({
    html: `<div style="background-color: #00A9E0; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; border: 3px solid white; box-shadow: 0 4px 15px rgba(0,169,224,0.4); font-size: 14px;">${cluster.getChildCount()}</div>`,
    className: 'tourism-cluster',
    iconSize: L.point(40, 40, true),
  });
};

const createIslamicClusterIcon = (cluster) => {
  return L.divIcon({
    html: `<div style="background-color: #2E8B57; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; border: 3px solid white; box-shadow: 0 4px 15px rgba(46,139,87,0.4); font-size: 14px;">${cluster.getChildCount()}</div>`,
    className: 'islamic-cluster',
    iconSize: L.point(40, 40, true),
  });
};

// Map controller component for flying to locations and handling resize
const MapController = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom, { duration: 1.5 });
    }
  }, [center, zoom, map]);

  // Invalidate map size on mount and resize
  useEffect(() => {
    // Initial invalidation with delay to ensure container is rendered
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 250);

    const handleResize = () => {
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, [map]);

  return null;
};

// Custom Zoom Control with Home Button
const CustomZoomControl = ({ bounds }) => {
  const map = useMap();
  const { language } = useLanguage();

  // Fit bounds when bounds change (initial load)
  useEffect(() => {
    if (bounds && Object.keys(bounds).length > 0) {
      map.fitBounds(bounds, { padding: [50, 50], duration: 1.5 });
    }
  }, [bounds, map]);

  const handleHome = (e) => {
    e.preventDefault();
    if (bounds && Object.keys(bounds).length > 0) {
      map.fitBounds(bounds, { padding: [50, 50], duration: 1.5 });
    } else {
      map.flyTo([42, 60], 4, { duration: 1.5 });
    }
  };

  return (
    <div className="leaflet-top leaflet-left">
      <div className="leaflet-control custom-zoom-control">
        <a href="#" onClick={handleHome} title={language === 'tr' ? 'Başlangıç görünümü' : 'Home'} role="button" aria-label={language === 'tr' ? 'Başlangıç görünümü' : 'Home'}>
          🏠
        </a>
        <a href="#" onClick={(e) => { e.preventDefault(); map.zoomIn(); }} title={language === 'tr' ? 'Yakınlaştır' : 'Zoom in'} role="button" aria-label={language === 'tr' ? 'Yakınlaştır' : 'Zoom in'}>
          +
        </a>
        <a href="#" onClick={(e) => { e.preventDefault(); map.zoomOut(); }} title={language === 'tr' ? 'Uzaklaştır' : 'Zoom out'} role="button" aria-label={language === 'tr' ? 'Uzaklaştır' : 'Zoom out'}>
          −
        </a>
      </div>
    </div>
  );
};

// Animated Silk Road Line Component
const AnimatedPolyline = ({ positions, color, name, category }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Polyline
      positions={positions}
      pathOptions={{
        color: isHovered ? '#fff' : color,
        weight: isHovered ? 6 : 4,
        opacity: isHovered ? 1 : 0.8,
        dashArray: '15, 10',
        lineCap: 'round',
        lineJoin: 'round',
        className: `animated-silk-road ${isHovered ? 'silk-road-glow' : ''}`,
      }}
      eventHandlers={{
        mouseover: () => setIsHovered(true),
        mouseout: () => setIsHovered(false),
      }}
    >
      <Popup>
        <div style={{ textAlign: 'center', padding: '10px' }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>🛤️ {name}</h3>
          {category && <span style={{ 
            background: '#FFD70030', 
            padding: '4px 12px', 
            borderRadius: '20px',
            fontSize: '12px',
            color: '#B8860B'
          }}>{category}</span>}
        </div>
      </Popup>
    </Polyline>
  );
};

// Turkic State Polygon Component
const TurkicStatePolygon = ({ positions, name, isDarkMode }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { language } = useLanguage();

  return (
    <Polygon
      positions={positions}
      pathOptions={{
        color: isHovered ? '#FFD700' : '#E30A17',
        weight: isHovered ? 3 : 2,
        opacity: 1,
        fillColor: '#E30A17',
        fillOpacity: isHovered ? 0.4 : 0.25,
        className: 'turkic-state-polygon',
      }}
      eventHandlers={{
        mouseover: () => setIsHovered(true),
        mouseout: () => setIsHovered(false),
      }}
    >
      <Popup>
        <div style={{ textAlign: 'center', padding: '10px' }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#E30A17' }}>🏛️ {name}</h3>
          <span style={{ 
            background: '#E30A1720', 
            padding: '4px 12px', 
            borderRadius: '20px',
            fontSize: '12px',
            color: '#E30A17'
          }}>{language === 'tr' ? 'Türk Devleti' : 'Turkic State'}</span>
        </div>
      </Popup>
    </Polygon>
  );
};

// Custom hook for window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const Map = () => {
  const { isDarkMode } = useTheme();
  const { language } = useLanguage();
  const { width } = useWindowSize();
  const isMobile = width <= 768;
  const isTablet = width > 768 && width <= 1024;
  
  const [mapCenter, setMapCenter] = useState([42, 60]);
  const [mapZoom, setMapZoom] = useState(4);
  const [initialBounds, setInitialBounds] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Layer data states
  const [turkicStates, setTurkicStates] = useState([]);
  const [tourismSites, setTourismSites] = useState([]);
  const [silkRoadRoutes, setSilkRoadRoutes] = useState([]);
  const [islamicHeritage, setIslamicHeritage] = useState([]);

  // Layer visibility states
  const [visibleLayers, setVisibleLayers] = useState({
    turkicStates: true,
    tourism: false,
    silkRoad: true,
    islamicHeritage: false,
  });

  // Selected feature state
  const [selectedFeature, setSelectedFeature] = useState(null);

  // Stats
  const [stats, setStats] = useState({
    states: 0,
    tourism: 0,
    routes: 0,
    heritage: 0,
  });

  // Update sidebar state when screen size changes
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Fetch layer data from local GeoJSON files
  const fetchLayerData = useCallback(async (layerId) => {
    const url = `${process.env.PUBLIC_URL}/data/${LAYER_DATA_FILES[layerId]}.geojson`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch layer ${layerId}`);
    return response.json();
  }, []);

  // Convert GeoJSON coordinates to Leaflet format
  const convertCoordinates = (coords, geometryType) => {
    if (geometryType === 'Point') {
      return [coords[1], coords[0]];
    } else if (geometryType === 'LineString') {
      return coords.map(coord => [coord[1], coord[0]]);
    } else if (geometryType === 'MultiLineString') {
      return coords.map(line => line.map(coord => [coord[1], coord[0]]));
    } else if (geometryType === 'Polygon') {
      return coords.map(ring => ring.map(coord => [coord[1], coord[0]]));
    } else if (geometryType === 'MultiPolygon') {
      return coords.map(polygon => polygon.map(ring => ring.map(coord => [coord[1], coord[0]])));
    }
    return coords;
  };

  // Load all layer data
  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all layers in parallel
        const [statesData, tourismData, silkRoadData, islamicData] = await Promise.all([
          fetchLayerData(0),
          fetchLayerData(1),
          fetchLayerData(2),
          fetchLayerData(3),
        ]);

        const allCoords = [];
        // Process Turkic States (Polygons)
        if (statesData.features) {
          const states = statesData.features.map(f => ({
            id: f.properties['postgregis.sde.tsr_trk_devletleri.fid'] || f.id,
            name: f.properties.name_tr || 'Unknown',
            coordinates: convertCoordinates(f.geometry.coordinates, f.geometry.type),
            geometryType: f.geometry.type,
          }));
          
          // Collect coords for bounds
          states.forEach(s => {
            if (s.geometryType === 'MultiPolygon') s.coordinates.flat(2).forEach(c => allCoords.push(c));
            else if (s.geometryType === 'Polygon') s.coordinates.flat(1).forEach(c => allCoords.push(c));
          });

          setTurkicStates(states);
          setStats(prev => ({ ...prev, states: states.length }));
        }

        // Process Tourism Sites (Points)
        if (tourismData.features) {
          const tourism = tourismData.features.map(f => ({
            id: f.properties['postgregis.sde.tsr_tourism_map.fid'] || f.id,
            name: f.properties.name || 'Unknown Site',
            photo: f.properties.photo,
            country: f.properties.country,
            type: f.properties.type || f.properties.category || f.properties.name,
            coordinates: convertCoordinates(f.geometry.coordinates, 'Point'),
          }));
          tourism.forEach(t => allCoords.push(t.coordinates));
          setTourismSites(tourism);
          setStats(prev => ({ ...prev, tourism: tourism.length }));
        }

        // Process Silk Road Routes (Lines)
        if (silkRoadData.features) {
          const routes = silkRoadData.features.map(f => ({
            id: f.properties['postgregis.sde.tsr_silk_road.fid'] || f.id,
            name: f.properties.name || 'Silk Road',
            category: f.properties.category,
            continent: f.properties.continent,
            coordinates: convertCoordinates(f.geometry.coordinates, f.geometry.type),
            geometryType: f.geometry.type,
          }));
          
          routes.forEach(r => {
            if (r.geometryType === 'MultiLineString') r.coordinates.flat(1).forEach(c => allCoords.push(c));
            else r.coordinates.forEach(c => allCoords.push(c));
          });
          setSilkRoadRoutes(routes);
          setStats(prev => ({ ...prev, routes: routes.length }));
        }

        // Process Islamic Heritage Sites (Points)
        if (islamicData.features) {
          const heritage = islamicData.features.map(f => ({
            id: f.properties['postgregis.sde.tsr_islamic_word_map.fid'] || f.id,
            name: f.properties.name || 'Unknown Site',
            city: f.properties.city,
            country: f.properties.country,
            photo: f.properties.photo,
            coordinates: convertCoordinates(f.geometry.coordinates, 'Point'),
          }));
          heritage.forEach(h => allCoords.push(h.coordinates));
          setIslamicHeritage(heritage);
          setStats(prev => ({ ...prev, heritage: heritage.length }));
        }

        // Calculate bounds
        if (allCoords.length > 0) {
          setInitialBounds(L.latLngBounds(allCoords));
        }

      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [fetchLayerData]);

  // Toggle layer visibility
  const toggleLayer = (layerKey) => {
    setVisibleLayers(prev => ({
      ...prev,
      [layerKey]: !prev[layerKey],
    }));
  };

  // Open external map
  const openExternalMap = (lat, lng, type) => {
    let url;
    if (type === 'direction') {
      url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    } else {
      url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    }
    window.open(url, '_blank');
  };

  // Dark mode tile layer (Esri Canvas: ücretsiz, API anahtarı gerektirmez)
  const tileUrl = isDarkMode
    ? 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}'
    : 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';

  // Silk Road color by category
  const getSilkRoadColor = (category) => {
    const colors = {
      'land': '#FFD700',
      'sea': '#00CED1',
      'default': '#FFA500',
    };
    return colors[category?.toLowerCase()] || colors.default;
  };

  // Calculate map height for mobile
  const mobileMapHeight = Math.max(350, window.innerHeight * 0.5);

  // Responsive styles
  const responsiveStyles = {
    mapContainer: {
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      height: isMobile ? 'auto' : 'calc(100vh - 250px)',
      minHeight: isMobile ? 'auto' : '600px',
      position: 'relative',
    },
    sidebar: {
      width: isMobile ? '100%' : isTablet ? '280px' : '320px',
      backgroundColor: isDarkMode ? DARK_THEME.surface : COLORS.white,
      borderRight: isMobile ? 'none' : `1px solid ${isDarkMode ? DARK_THEME.border : COLORS.gray300}`,
      borderTop: isMobile ? `1px solid ${isDarkMode ? DARK_THEME.border : COLORS.gray300}` : 'none',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      order: isMobile ? 2 : 1,
      maxHeight: isMobile ? (sidebarOpen ? '400px' : '60px') : 'none',
      transition: 'max-height 0.3s ease',
      flexShrink: 0,
    },
    mapWrapper: {
      flex: isMobile ? 'none' : 1,
      position: 'relative',
      order: isMobile ? 1 : 2,
      height: isMobile ? `${mobileMapHeight}px` : '100%',
      minHeight: isMobile ? '350px' : '600px',
      width: '100%',
    },
    legend: {
      position: 'absolute',
      bottom: isMobile ? '10px' : '20px',
      left: isMobile ? '10px' : '20px',
      padding: isMobile ? '10px 14px' : '15px 20px',
      borderRadius: '16px',
      zIndex: 1000,
      backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.85)' : 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(12px)',
      boxShadow: isDarkMode ? `0 8px 32px rgba(0,0,0,0.3)` : '0 8px 32px rgba(0,0,0,0.1)',
      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.5)'}`,
      maxWidth: isMobile ? '160px' : 'auto',
    },
    layerItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: isMobile ? '10px 12px' : '12px 15px',
      borderRadius: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
      gap: isMobile ? '8px' : '10px',
    },
    statCard: {
      padding: isMobile ? '10px 8px' : '15px',
      borderRadius: '12px',
      textAlign: 'center',
      backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light,
      border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'}`,
    },
  };

  return (
    <div style={styles.page}>
      {/* Hero */}
      <section style={{
        ...styles.hero,
        padding: isMobile ? '120px 15px 40px' : '140px 20px 60px',
        minHeight: isMobile ? '200px' : '280px',
        background: isDarkMode
          ? `linear-gradient(180deg, ${DARK_THEME.backgroundTertiary}DD 0%, ${DARK_THEME.background}DD 100%), url(${mapHeroImage})`
          : `linear-gradient(180deg, ${COLORS.primaryDark}DD 0%, ${COLORS.dark}DD 100%), url(${mapHeroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundBlendMode: 'multiply',
      }}>
        <div style={styles.heroOverlay}></div>
        <div style={styles.heroContent}>
          <span style={{
            ...styles.badge,
            padding: isMobile ? '6px 16px' : '8px 24px',
            fontSize: isMobile ? '12px' : '14px',
            backgroundColor: isDarkMode ? `${DARK_THEME.primary}25` : 'rgba(255,255,255,0.15)',
            color: isDarkMode ? DARK_THEME.primary : COLORS.white,
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255,255,255,0.1)',
          }}>{language === 'tr' ? 'İnteraktif Harita' : 'Interactive Map'}</span>
          <h1 style={{
            ...styles.heroTitle,
            fontSize: isMobile ? 'clamp(24px, 6vw, 32px)' : 'clamp(32px, 5vw, 48px)',
          }}>{language === 'tr' ? 'Türk Dünyasını Keşfedin' : 'Explore the Turkic World'}</h1>
          <p style={{
            ...styles.heroSubtitle,
            fontSize: isMobile ? '14px' : '17px',
            padding: isMobile ? '0 10px' : '0',
          }}>
            {language === 'tr'
              ? 'Türk devletleri boyunca tarihi İpek Yolu bağlantılarını, turizm destinasyonlarını ve kültürel mirası keşfedin'
              : 'Discover the historic Silk Road connections, tourism destinations, and cultural heritage across Turkic nations'}
          </p>
        </div>
      </section>

      {/* Map Section */}
      <section style={{
        ...styles.mapSection,
        backgroundColor: isDarkMode ? DARK_THEME.background : COLORS.white,
      }}>
        <div style={responsiveStyles.mapContainer}>
          {/* Sidebar */}
          <div style={responsiveStyles.sidebar}>
            {/* Mobile Toggle Header */}
            {isMobile && (
              <div 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '15px 20px',
                  cursor: 'pointer',
                  backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light,
                  borderBottom: sidebarOpen ? `1px solid ${isDarkMode ? DARK_THEME.border : COLORS.gray300}` : 'none',
                }}
              >
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark,
                }}>
                  🗺️ {language === 'tr' ? 'Harita Kontrolleri' : 'Map Controls'}
                </span>
                <span style={{
                  fontSize: '20px',
                  transform: sidebarOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                }}>
                  ▼
                </span>
              </div>
            )}

            {/* Sidebar Content */}
            <div style={{
              display: sidebarOpen || !isMobile ? 'block' : 'none',
              overflow: 'auto',
              flex: 1,
            }}>
              {/* Layer Controls */}
              <div style={{
                ...styles.layerSection,
                padding: isMobile ? '15px' : '20px',
              }}>
                {!isMobile && (
                  <h3 style={{...styles.sectionTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                    🗺️ {language === 'tr' ? 'Harita Katmanları' : 'Map Layers'}
                  </h3>
                )}
                <div style={styles.layerList}>
                  {Object.entries(LAYERS).map(([key, layer]) => (
                    <div
                      key={key}
                      onClick={() => toggleLayer(key)}
                      className="modern-layer-hover"
                      style={{
                        ...responsiveStyles.layerItem,
                        backgroundColor: visibleLayers[key]
                          ? (isDarkMode ? `${layer.color}20` : `${layer.color}15`)
                          : (isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light),
                        borderLeft: `4px solid ${visibleLayers[key] ? layer.color : 'transparent'}`,
                        opacity: visibleLayers[key] ? 1 : 0.6,
                      }}
                    >
                      <div style={styles.layerItemLeft}>
                        <span style={{ fontSize: isMobile ? '18px' : '20px' }}>{layer.icon}</span>
                        <span style={{
                          fontSize: isMobile ? '13px' : '14px',
                          fontWeight: '500',
                          color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark,
                        }}>{language === 'tr' ? layer.nameTr : layer.name}</span>
                      </div>
                      <div style={{
                        ...styles.layerToggle,
                        width: isMobile ? '36px' : '40px',
                        height: isMobile ? '20px' : '24px',
                        backgroundColor: visibleLayers[key] ? layer.color : (isDarkMode ? DARK_THEME.border : COLORS.gray300),
                      }}>
                        <div style={{
                          ...styles.layerToggleKnob,
                          width: isMobile ? '16px' : '20px',
                          height: isMobile ? '16px' : '20px',
                          transform: visibleLayers[key] ? 'translateX(16px)' : 'translateX(0)',
                        }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats Section */}
              <div style={{
                ...styles.statsSection,
                padding: isMobile ? '15px' : '20px',
              }}>
                {!isMobile && (
                  <h3 style={{...styles.sectionTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                    📊 {language === 'tr' ? 'İstatistikler' : 'Statistics'}
                  </h3>
                )}
                <div style={responsiveStyles.statsGrid}>
                  <div style={responsiveStyles.statCard} className="modern-card-hover">
                    <span style={{ fontSize: isMobile ? '18px' : '24px', display: 'block', marginBottom: isMobile ? '4px' : '8px' }}>🏛️</span>
                    <span style={{...styles.statValue, fontSize: isMobile ? '18px' : '24px', color: LAYERS.turkicStates.color}}>{stats.states}</span>
                    <span style={{...styles.statLabel, fontSize: isMobile ? '9px' : '11px', color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray500}}>{language === 'tr' ? 'Devlet' : 'States'}</span>
                  </div>
                  <div style={responsiveStyles.statCard} className="modern-card-hover">
                    <span style={{ fontSize: isMobile ? '18px' : '24px', display: 'block', marginBottom: isMobile ? '4px' : '8px' }}>📸</span>
                    <span style={{...styles.statValue, fontSize: isMobile ? '18px' : '24px', color: LAYERS.tourism.color}}>{stats.tourism}</span>
                    <span style={{...styles.statLabel, fontSize: isMobile ? '9px' : '11px', color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray500}}>{language === 'tr' ? 'Nokta' : 'Sites'}</span>
                  </div>
                  <div style={responsiveStyles.statCard} className="modern-card-hover">
                    <span style={{ fontSize: isMobile ? '18px' : '24px', display: 'block', marginBottom: isMobile ? '4px' : '8px' }}>🛤️</span>
                    <span style={{...styles.statValue, fontSize: isMobile ? '18px' : '24px', color: LAYERS.silkRoad.color}}>{stats.routes}</span>
                    <span style={{...styles.statLabel, fontSize: isMobile ? '9px' : '11px', color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray500}}>{language === 'tr' ? 'Rota' : 'Routes'}</span>
                  </div>
                  <div style={responsiveStyles.statCard} className="modern-card-hover">
                    <span style={{ fontSize: isMobile ? '18px' : '24px', display: 'block', marginBottom: isMobile ? '4px' : '8px' }}>🕌</span>
                    <span style={{...styles.statValue, fontSize: isMobile ? '18px' : '24px', color: LAYERS.islamicHeritage.color}}>{stats.heritage}</span>
                    <span style={{...styles.statLabel, fontSize: isMobile ? '9px' : '11px', color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray500}}>{language === 'tr' ? 'Miras' : 'Heritage'}</span>
                  </div>
                </div>
              </div>

              {/* Selected Feature Info */}
              {selectedFeature && !isMobile && (
                <div style={styles.featureSection}>
                  <h3 style={{...styles.sectionTitle, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                    📍 {language === 'tr' ? 'Seçilen' : 'Selected'}
                  </h3>
                  <div style={{
                    ...styles.featureCard,
                    backgroundColor: isDarkMode ? DARK_THEME.backgroundSecondary : COLORS.light,
                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'}`,
                  }}>
                    {selectedFeature.photo && (
                      <img 
                        src={selectedFeature.photo} 
                        alt={selectedFeature.name}
                        style={styles.featureImage}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <h4 style={{...styles.featureName, color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark}}>
                      {selectedFeature.name}
                    </h4>
                    {selectedFeature.country && (
                      <span style={{...styles.featureCountry, color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray500}}>
                        📍 {selectedFeature.city ? `${selectedFeature.city}, ` : ''}{selectedFeature.country}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Loading / Error States */}
              {loading && (
                <div style={{...styles.loadingContainer, padding: isMobile ? '20px' : '30px'}}>
                  <div style={styles.spinner}></div>
                  <span style={{...styles.loadingText, color: isDarkMode ? DARK_THEME.textMuted : COLORS.gray500}}>
                    {language === 'tr' ? 'Harita verileri yükleniyor...' : 'Loading map data...'}
                  </span>
                </div>
              )}
              {error && (
                <div style={styles.errorContainer}>
                  <span style={styles.errorIcon}>⚠️</span>
                  <span style={styles.errorText}>
                    {language === 'tr' ? 'Harita verileri yüklenirken bir hata oluştu.' : 'An error occurred while loading map data.'}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          <div style={responsiveStyles.mapWrapper} className="map-wrapper-container">
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              style={{ 
                width: '100%', 
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
              scrollWheelZoom={true}
              zoomControl={false}
            >
              <MapController center={mapCenter} zoom={mapZoom} />
              <CustomZoomControl bounds={initialBounds} />
              <TileLayer
                attribution='&copy; Esri &mdash; Esri, DeLorme, NAVTEQ'
                url={tileUrl}
                maxNativeZoom={16}
                maxZoom={18}
              />

              {/* Turkic States Layer */}
              {visibleLayers.turkicStates && turkicStates.map((state) => {
                if (state.geometryType === 'MultiPolygon') {
                  return state.coordinates.map((polygon, idx) => (
                    <TurkicStatePolygon
                      key={`${state.id}-${idx}`}
                      positions={polygon}
                      name={state.name}
                      isDarkMode={isDarkMode}
                    />
                  ));
                }
                return (
                  <TurkicStatePolygon
                    key={state.id}
                    positions={state.coordinates}
                    name={state.name}
                    isDarkMode={isDarkMode}
                  />
                );
              })}

              {/* Silk Road Routes Layer */}
              {visibleLayers.silkRoad && silkRoadRoutes.map((route) => {
                const color = getSilkRoadColor(route.category);
                if (route.geometryType === 'MultiLineString') {
                  return route.coordinates.map((line, idx) => (
                    <AnimatedPolyline
                      key={`${route.id}-${idx}`}
                      positions={line}
                      color={color}
                      name={route.name}
                      category={route.category}
                    />
                  ));
                }
                return (
                  <AnimatedPolyline
                    key={route.id}
                    positions={route.coordinates}
                    color={color}
                    name={route.name}
                    category={route.category}
                  />
                );
              })}

              {/* Tourism Sites Layer */}
              {visibleLayers.tourism && (
                <MarkerClusterGroup
                  chunkedLoading
                  iconCreateFunction={createTourismClusterIcon}
                  spiderfyOnMaxZoom={true}
                  showCoverageOnHover={false}
                  maxClusterRadius={60}
                >
                  {tourismSites.map((site) => (
                    <Marker
                      key={site.id}
                      position={site.coordinates}
                      icon={createTourismIcon(site.type)}
                      eventHandlers={{
                        click: () => {
                          setSelectedFeature(site);
                          setMapCenter(site.coordinates);
                          setMapZoom(12);
                        },
                      }}
                    >
                      <Popup>
                        <div style={styles.popup}>
                          {site.photo && (
                            <img 
                              src={site.photo} 
                              alt={site.name}
                              style={styles.popupImage}
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <h3 style={styles.popupTitle}>📸 {site.name}</h3>
                          {site.country && (
                            <span style={styles.popupCountry}>📍 {site.country}</span>
                          )}
                          <div style={styles.popupButtons}>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                openExternalMap(site.coordinates[0], site.coordinates[1], 'view');
                              }}
                              style={{...styles.popupBtn, backgroundColor: '#00A9E0'}}
                            >
                              🗺️ {language === 'tr' ? 'Konuma Git' : 'View on Map'}
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                openExternalMap(site.coordinates[0], site.coordinates[1], 'direction');
                              }}
                              style={{...styles.popupBtn, backgroundColor: '#2E8B57'}}
                            >
                              🚗 {language === 'tr' ? 'Yol Tarifi' : 'Directions'}
                            </button>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}

              {/* Islamic Heritage Layer */}
              {visibleLayers.islamicHeritage && (
                <MarkerClusterGroup
                  chunkedLoading
                  iconCreateFunction={createIslamicClusterIcon}
                  spiderfyOnMaxZoom={true}
                  showCoverageOnHover={false}
                  maxClusterRadius={60}
                >
                  {islamicHeritage.map((site) => (
                    <Marker
                      key={site.id}
                      position={site.coordinates}
                      icon={createIslamicIcon()}
                      eventHandlers={{
                        click: () => {
                          setSelectedFeature(site);
                          setMapCenter(site.coordinates);
                          setMapZoom(12);
                        },
                      }}
                    >
                      <Popup>
                        <div style={styles.popup}>
                          {site.photo && (
                            <img 
                              src={site.photo} 
                              alt={site.name}
                              style={styles.popupImage}
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <h3 style={styles.popupTitle}>🕌 {site.name}</h3>
                          {(site.city || site.country) && (
                            <span style={styles.popupCountry}>
                              📍 {site.city ? `${site.city}, ` : ''}{site.country}
                            </span>
                          )}
                          <div style={styles.popupButtons}>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                openExternalMap(site.coordinates[0], site.coordinates[1], 'view');
                              }}
                              style={{...styles.popupBtn, backgroundColor: '#00A9E0'}}
                            >
                              🗺️ {language === 'tr' ? 'Konuma Git' : 'View on Map'}
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                openExternalMap(site.coordinates[0], site.coordinates[1], 'direction');
                              }}
                              style={{...styles.popupBtn, backgroundColor: '#2E8B57'}}
                            >
                              🚗 {language === 'tr' ? 'Yol Tarifi' : 'Directions'}
                            </button>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MarkerClusterGroup>
              )}
            </MapContainer>

            {/* Map Legend */}
            <div style={responsiveStyles.legend}>
              <h4 style={{
                ...styles.legendTitle, 
                fontSize: isMobile ? '10px' : '12px',
                marginBottom: isMobile ? '8px' : '12px',
                color: isDarkMode ? DARK_THEME.textPrimary : COLORS.dark
              }}>
                {language === 'tr' ? 'Lejant' : 'Legend'}
              </h4>
              <div style={{...styles.legendItem, marginBottom: isMobile ? '6px' : '8px'}}>
                <div style={{...styles.legendPolygon, width: isMobile ? '18px' : '24px', height: isMobile ? '12px' : '16px', backgroundColor: '#E30A1740', border: '2px solid #E30A17'}}></div>
                <span style={{...styles.legendLabel, fontSize: isMobile ? '10px' : '12px', color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                  {language === 'tr' ? 'Türk Devletleri' : 'Turkic States'}
                </span>
              </div>
              <div style={{...styles.legendItem, marginBottom: isMobile ? '6px' : '8px'}}>
                <div style={{...styles.legendLine, width: isMobile ? '22px' : '30px', height: isMobile ? '3px' : '4px', backgroundColor: '#FFD700'}}></div>
                <span style={{...styles.legendLabel, fontSize: isMobile ? '10px' : '12px', color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                  {language === 'tr' ? 'İpek Yolu' : 'Silk Road'}
                </span>
              </div>
              <div style={{...styles.legendItem, marginBottom: isMobile ? '6px' : '8px'}}>
                <span style={{...styles.legendMarker, fontSize: isMobile ? '14px' : '18px'}}>📸</span>
                <span style={{...styles.legendLabel, fontSize: isMobile ? '10px' : '12px', color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                  {language === 'tr' ? 'Turizm' : 'Tourism'}
                </span>
              </div>
              <div style={{...styles.legendItem, marginBottom: 0}}>
                <span style={{...styles.legendMarker, fontSize: isMobile ? '14px' : '18px'}}>🕌</span>
                <span style={{...styles.legendLabel, fontSize: isMobile ? '10px' : '12px', color: isDarkMode ? DARK_THEME.textSecondary : COLORS.gray600}}>
                  {language === 'tr' ? 'Miras' : 'Heritage'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section style={{
        ...styles.infoSection,
        padding: isMobile ? '50px 15px' : '80px 20px',
        background: isDarkMode
          ? `linear-gradient(180deg, ${DARK_THEME.backgroundTertiary} 0%, ${DARK_THEME.background} 100%)`
          : COLORS.darkGradient,
      }}>
        <div style={styles.infoContainer}>
          <div style={styles.infoHeader}>
            <span style={{
              ...styles.infoBadge,
              padding: isMobile ? '6px 16px' : '8px 24px',
              fontSize: isMobile ? '12px' : '14px',
            }}>{language === 'tr' ? 'Harita Hakkında' : 'About the Map'}</span>
            <h2 style={{
              ...styles.infoTitle,
              fontSize: isMobile ? 'clamp(22px, 5vw, 28px)' : 'clamp(28px, 4vw, 40px)',
            }}>{language === 'tr' ? 'Modern İpek Yolu' : 'The Modern Silk Road'}</h2>
            <p style={{
              ...styles.infoSubtitle,
              fontSize: isMobile ? '14px' : '17px',
            }}>
              {language === 'tr'
                ? 'Ticaret, kültür ve diplomasi yoluyla medeniyetleri birbirine bağlıyor'
                : 'Connecting civilizations through trade, culture, and diplomacy'}
            </p>
          </div>
          <div style={{
            ...styles.infoGrid,
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: isMobile ? '15px' : '25px',
          }}>
            <div style={{
              ...styles.infoCard,
              padding: isMobile ? '25px' : '35px',
              backgroundColor: isDarkMode ? DARK_THEME.surface : 'rgba(255,255,255,0.08)',
            }}>
              <span style={{...styles.infoIcon, fontSize: isMobile ? '32px' : '42px'}}>🛤️</span>
              <h3 style={{...styles.infoCardTitle, fontSize: isMobile ? '18px' : '20px'}}>{language === 'tr' ? 'Tarihi Rotalar' : 'Historic Routes'}</h3>
              <p style={{...styles.infoCardText, fontSize: isMobile ? '14px' : '15px'}}>
                {language === 'tr'
                  ? 'Antik İpek Yolu, Doğu ile Batı arasında köprü kurarak yüzyıllar boyunca ticareti, kültürel alışverişi ve diplomatik ilişkileri mümkün kılmıştır.'
                  : 'The ancient Silk Road connected East and West, facilitating trade, cultural exchange, and diplomatic relations for centuries.'}
              </p>
            </div>
            <div style={{
              ...styles.infoCard,
              padding: isMobile ? '25px' : '35px',
              backgroundColor: isDarkMode ? DARK_THEME.surface : 'rgba(255,255,255,0.08)',
            }}>
              <span style={{...styles.infoIcon, fontSize: isMobile ? '32px' : '42px'}}>🕌</span>
              <h3 style={{...styles.infoCardTitle, fontSize: isMobile ? '18px' : '20px'}}>{language === 'tr' ? 'İslami Miras' : 'Islamic Heritage'}</h3>
              <p style={{...styles.infoCardText, fontSize: isMobile ? '14px' : '15px'}}>
                {language === 'tr'
                  ? 'Türk dünyasına ve ötesine yayılmış zengin İslami mimari ve kültürel miras alanlarını keşfedin.'
                  : 'Discover the rich Islamic architectural and cultural heritage sites scattered across the Turkic world and beyond.'}
              </p>
            </div>
            <div style={{
              ...styles.infoCard,
              padding: isMobile ? '25px' : '35px',
              backgroundColor: isDarkMode ? DARK_THEME.surface : 'rgba(255,255,255,0.08)',
            }}>
              <span style={{...styles.infoIcon, fontSize: isMobile ? '32px' : '42px'}}>📸</span>
              <h3 style={{...styles.infoCardTitle, fontSize: isMobile ? '18px' : '20px'}}>{language === 'tr' ? 'Turizm Destinasyonları' : 'Tourism Destinations'}</h3>
              <p style={{...styles.infoCardText, fontSize: isMobile ? '14px' : '15px'}}>
                {language === 'tr'
                  ? 'Antik şehirlerden doğa harikalarına, Türk devletlerindeki büyüleyici turizm destinasyonlarını keşfedin.'
                  : 'Explore breathtaking tourism destinations across Turkic nations, from ancient cities to natural wonders.'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const styles = {
  page: { },
  hero: { 
    textAlign: 'center',
    position: 'relative',
        height: '50vh',
    minHeight: '400px',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0L60 30L30 60L0 30z\' fill=\'rgba(255,255,255,0.03)\'/%3E%3C/svg%3E")',
    pointerEvents: 'none',
  },
  heroContent: { maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 },
  badge: { display: 'inline-block', borderRadius: '50px', fontWeight: '600', marginBottom: '20px' },
  heroTitle: { color: COLORS.white, fontWeight: '700', marginBottom: '16px' },
  heroSubtitle: { color: 'rgba(255,255,255,0.8)', lineHeight: '1.7' },
  mapSection: { padding: '0' },
  sectionTitle: { fontSize: '14px', fontWeight: '600', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  layerSection: { borderBottom: '1px solid rgba(0,0,0,0.1)' },
  layerList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  layerItemLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  layerToggle: { borderRadius: '12px', position: 'relative', transition: 'all 0.2s ease' },
  layerToggleKnob: { backgroundColor: '#fff', borderRadius: '50%', position: 'absolute', top: '2px', left: '2px', transition: 'all 0.2s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' },
  statsSection: { borderBottom: '1px solid rgba(0,0,0,0.1)' },
  statValue: { fontWeight: '700', display: 'block' },
  statLabel: { textTransform: 'uppercase', letterSpacing: '0.5px' },
  featureSection: { padding: '20px' },
  featureCard: { padding: '15px', borderRadius: '12px' },
  featureImage: { width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', marginBottom: '12px' },
  featureName: { fontSize: '16px', fontWeight: '600', marginBottom: '6px' },
  featureCountry: { fontSize: '13px' },
  loadingContainer: { textAlign: 'center' },
  spinner: { width: '40px', height: '40px', border: '3px solid rgba(0,0,0,0.1)', borderTop: '3px solid #E30A17', borderRadius: '50%', margin: '0 auto 15px', animation: 'spin 1s linear infinite' },
  loadingText: { fontSize: '14px' },
  errorContainer: { padding: '20px', textAlign: 'center' },
  errorIcon: { fontSize: '32px', display: 'block', marginBottom: '10px' },
  errorText: { fontSize: '14px', color: '#dc3545' },
  map: { width: '100%', height: '100%' },
  legendTitle: { fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '10px' },
  legendPolygon: { borderRadius: '3px' },
  legendLine: { borderRadius: '2px' },
  legendMarker: { width: '24px', textAlign: 'center' },
  legendLabel: { },
  popup: { minWidth: '200px', textAlign: 'center' },
  popupImage: { width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px', marginBottom: '12px' },
  popupTitle: { fontSize: '15px', fontWeight: '600', color: '#333', marginBottom: '8px' },
  popupCountry: { fontSize: '13px', color: '#666' },
  popupButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
    justifyContent: 'center',
    width: '100%',
  },
  popupBtn: {
    flex: 1,
    padding: '8px 4px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    color: '#fff',
    transition: 'transform 0.2s',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
  },
  infoSection: { },
  infoContainer: { maxWidth: '1100px', margin: '0 auto' },
  infoHeader: { textAlign: 'center', marginBottom: '50px' },
  infoBadge: { display: 'inline-block', backgroundColor: 'rgba(255,255,255,0.15)', color: COLORS.white, borderRadius: '50px', fontWeight: '600', marginBottom: '20px' },
  infoTitle: { color: COLORS.white, fontWeight: '700', marginBottom: '15px' },
  infoSubtitle: { color: 'rgba(255,255,255,0.75)', maxWidth: '600px', margin: '0 auto' },
  infoGrid: { display: 'grid' },
  infoCard: { borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)' },
  infoIcon: { marginBottom: '20px', display: 'block' },
  infoCardTitle: { color: COLORS.white, fontWeight: '600', marginBottom: '12px' },
  infoCardText: { color: 'rgba(255,255,255,0.7)', lineHeight: '1.7' },
};

export default Map;
